import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import AnimalSymbol from "@/components/AnimalSymbol";
import { BODY_PARTS, QUALITIES, QUALITY_COLOR, emptyParts } from "@/lib/quality";
import { CONDITION_FIELDS } from "@/lib/conditions";
import { formatUS } from "@/lib/dates";
import { ChevronLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QLabel = Object.fromEntries(QUALITIES.map((q) => [q.value, q.label]));

const Row = ({ label, left, right, match }) => (
  <div className="grid grid-cols-[150px_1fr_1fr] gap-3 py-2 border-b border-[var(--app-border)] last:border-0 text-sm">
    <div className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)] pt-1.5 pr-2 whitespace-nowrap">{label}</div>
    <div className={`${match ? "bg-blue-50" : ""} rounded px-2 py-1 min-w-0 break-words`}>{left}</div>
    <div className={`${match ? "bg-blue-50" : ""} rounded px-2 py-1 min-w-0 break-words`}>{right}</div>
  </div>
);

export default function ComparePage() {
  const nav = useNavigate();
  const [animals, setAnimals] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");

  useEffect(() => {
    axios.get(`${API}/animals`).then((r) => setAnimals(r.data));
  }, []);

  const a = useMemo(() => animals.find((x) => x.id === aId) || null, [animals, aId]);
  const b = useMemo(() => animals.find((x) => x.id === bId) || null, [animals, bId]);

  const Picker = ({ id, value, onChange, label }) => (
    <div className="flex-1 min-w-[220px]">
      <Label className="text-[10px] uppercase tracking-wider text-[var(--app-text-soft)]">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={`compare-pick-${id}`}><SelectValue placeholder="Choose an animal…" /></SelectTrigger>
        <SelectContent>
          {animals.map((x) => (
            <SelectItem key={x.id} value={x.id}>{x.name} <span className="text-[var(--app-text-soft)]">· {x.sex}</span></SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--app-bg)]">
      <Toaster richColors position="top-right" />
      <header className="h-14 border-b border-[var(--app-border)] bg-white px-5 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => nav("/")} data-testid="back-library">
          <ChevronLeft className="w-4 h-4 mr-1" /> Library
        </Button>
        <div className="w-px h-7 bg-[var(--app-border)]" />
        <div className="font-display font-extrabold text-lg">Compare animals</div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex gap-3 flex-wrap mb-6">
          <Picker id="left" value={aId} onChange={setAId} label="Animal A" />
          <Picker id="right" value={bId} onChange={setBId} label="Animal B" />
        </div>

        {!a || !b ? (
          <div className="text-center py-16 border border-dashed border-[var(--app-border)] rounded text-sm text-[var(--app-text-soft)]">
            Pick two animals from your library to compare structure & health.
          </div>
        ) : (
          <div className="bg-white border border-[var(--app-border)] rounded-md p-5" data-testid="compare-panel">
            {/* Stick figures */}
            <div className="grid grid-cols-2 gap-6 items-center mb-5">
              {[a, b].map((x) => (
                <div key={x.id} className="text-center">
                  <AnimalSymbol parts={x.parts || emptyParts()} size={140} />
                  <div className="font-display font-extrabold text-lg mt-2">{x.name}</div>
                  <div className="text-xs text-[var(--app-text-soft)]">{x.species_or_breed || "—"} · {x.sex}{x.dob ? ` · ${formatUS(x.dob)}` : ""}</div>
                </div>
              ))}
            </div>

            {/* Structure parts */}
            <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mb-2">Structure</div>
            {BODY_PARTS.map((bp) => {
              const qa = (a.parts || {})[bp.key] || "unknown";
              const qb = (b.parts || {})[bp.key] || "unknown";
              const Pill = ({ q }) => (
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm border border-black/10" style={{ backgroundColor: QUALITY_COLOR[q] }} />
                  <span>{QLabel[q] || q}</span>
                </span>
              );
              return <Row key={bp.key} label={bp.label} left={<Pill q={qa} />} right={<Pill q={qb} />} match={qa === qb && qa !== "unknown"} />;
            })}

            {/* Health */}
            <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mt-5 mb-2">Health</div>
            {CONDITION_FIELDS.map((f) => (
              <Row key={f.key} label={f.label} left={a[f.key] || "—"} right={b[f.key] || "—"} match={false} />
            ))}

            {/* Notes */}
            <div className="text-[10px] uppercase tracking-[0.22em] font-display font-semibold text-[var(--app-text-soft)] mt-5 mb-2">Notes</div>
            <Row label="Registration" left={a.registration || "—"} right={b.registration || "—"} />
            <Row label="Title" left={a.title || "—"} right={b.title || "—"} />
            <Row label="Notes" left={a.notes || "—"} right={b.notes || "—"} />
          </div>
        )}
      </main>
    </div>
  );
}
